import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface RegistryProfile {
    id: string;
    alias: string;
    display_name?: string;
    kind: 'local' | 'cloud' | 'partner';
    nats_url?: string;
    registry_subject?: string;
    base_url?: string;
    is_default: boolean;
    auth_type: string;
    auth_config: Record<string, unknown>;
    trust_tier: 'trusted' | 'verified' | 'untrusted';
    signer_keys: string[];
    supports_federation: boolean;
    supports_search: boolean;
    status: 'active' | 'disabled' | 'unreachable';
    last_seen_at?: string;
    zone?: string;
    config?: Record<string, unknown>;
    created: string;
    modified: string;
}

export interface RegistryTestResult {
    reachable: boolean;
    httpOk?: boolean;
    natsOk?: boolean;
    latencyMs: number;
    version?: string;
}

export function useRegistries() {
    return useQuery<RegistryProfile[]>({
        queryKey: ['registries'],
        queryFn: () =>
            api.get('/registries').then((r) => r.data?.data ?? r.data ?? []),
    });
}

export function useRegistry(registryId: string) {
    return useQuery<RegistryProfile>({
        queryKey: ['registry', registryId],
        queryFn: () => api.get(`/registries/${registryId}`).then((r) => r.data),
        enabled: !!registryId,
    });
}

export function useCreateRegistry() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: Partial<RegistryProfile>) =>
            api.post('/registries', data).then((r) => r.data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['registries'] }),
    });
}

export function useUpdateRegistry(registryId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: Partial<RegistryProfile>) =>
            api.put(`/registries/${registryId}`, data).then((r) => r.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['registries'] });
            queryClient.invalidateQueries({ queryKey: ['registry', registryId] });
        },
    });
}

export function useDeleteRegistry() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (registryId: string) =>
            api.delete(`/registries/${registryId}`).then((r) => r.data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['registries'] }),
    });
}

export function useTestRegistry(registryId: string) {
    const queryClient = useQueryClient();
    return useMutation<RegistryTestResult>({
        mutationFn: () =>
            api.post(`/registries/${registryId}/test`).then((r) => r.data),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['registry', registryId] }),
    });
}
